import { cssThemedValue } from '@creditornot/cb-ingredients';
import { ModalHeader } from '@creditornot/cb-modal';
import { Interval } from 'luxon/src/interval';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { WoltModal } from 'common/components';

import usePerformanceByVenueTableProps from './hooks/usePerformanceByVenueTableProps';
import usePerformanceByVenueInsights from '../../hooks/usePerformanceByVenueInsights';
import { InsightEntity } from '../../types';
import LoadingView from '../LoadingView';
import PerformanceByVenueTable, { VenueTableSortableColumnIDs } from '../tables/performance/PerformanceByVenueTable';
import { SortState } from '../tables/types';

type PerformanceByVenueModalProps = {
  isVATEnabled: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  insightEntity: Exclude<InsightEntity, { type: 'venue' }>;
  dateInterval: Interval;
};

const Root = styled(WoltModal)`
  width: 90vw;
  height: 90vh;
  max-height: 90%;
  padding: 0;
  border-radius: 8px;
  background-color: ${cssThemedValue('surfaceMain')};

  display: flex;
  flex-direction: column;
`;

const PerformanceByVenueModal = ({
  isOpen,
  onClose,
  insightEntity,
  dateInterval,
  isVATEnabled,
}: PerformanceByVenueModalProps) => {
  const entityId = insightEntity.id;

  const [sort, setSort] = useState<SortState<VenueTableSortableColumnIDs> | undefined>(undefined);

  const performanceByVenueInsights = usePerformanceByVenueInsights({
    entityType: insightEntity.type,
    entityId,
    interval: dateInterval,
    sortBy: sort?.sortBy,
    sortOrder: sort?.sortOrder,
    limit: undefined,
  });

  const tableProps = usePerformanceByVenueTableProps({
    isVATEnabled,
    sort,
    setSort,
    performanceByVenueInsights,
    isInfinite: true,
  });

  return (
    <Root isOpen={!!isOpen} onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="insights.performance.table.performance-by-venue" />
      </ModalHeader>
      {performanceByVenueInsights.isLoading && performanceByVenueInsights.isFetching ? (
        <LoadingView />
      ) : (
        <PerformanceByVenueTable {...tableProps} />
      )}
    </Root>
  );
};

export default PerformanceByVenueModal;
